/**
 * Owner-facing First Trust — first-bind enrollment and factory-reset re-enrollment.
 * No device-held secret. Bulk UI is convenience only: one window per serial.
 */

export type FirstTrustPhase =
  | "ready"
  | "awaiting"
  | "expired"
  | "revoked"
  | "not-enrolled"
  | "enrolled"
  | "ineligible";

export type FirstTrustPurpose = "first-bind" | "re-enroll";

/** Canonical operator-facing trust lifecycle (Device Assignments Status). */
export type TrustLifecycleStatus =
  | "enrollment-required"
  | "re-enrollment-required"
  | "enrolled";

export type FirstTrustInput = {
  serialNumber?: string;
  inventoryStatus?: string;
  activationStatus?: string;
  enrollmentStatus?: string;
  credentialBound?: boolean;
  firstTrustStatus?: string;
  firstTrustExpiresAt?: string;
  firstTrustWindowId?: string;
  firstTrustPurpose?: string;
  pendingTrustKind?: string;
  pendingTrustDeviceId?: string;
};

function isExpiredOpen(row: FirstTrustInput, nowMs: number): boolean {
  const ft = (row.firstTrustStatus || "").toLowerCase();
  if (ft === "expired") return true;
  if (ft !== "open") return false;
  const exp = row.firstTrustExpiresAt ? Date.parse(row.firstTrustExpiresAt) : NaN;
  return Number.isFinite(exp) && exp <= nowMs;
}

export function isCredentialBound(row: FirstTrustInput): boolean {
  return (
    row.credentialBound === true ||
    (row.enrollmentStatus || "").toLowerCase() === "enrolled"
  );
}

function pendingKind(row: FirstTrustInput): string {
  return (row.pendingTrustKind || "").toLowerCase().replace(/_/g, "-");
}

/**
 * Warehouse first-bind eligibility (also used as default bulk selection).
 */
export function isFirstTrustOpenEligible(
  row: FirstTrustInput,
  nowMs: number = Date.now(),
): boolean {
  const serial = (row.serialNumber || "").trim();
  if (!serial) return false;
  if (isCredentialBound(row)) return false;
  if ((row.inventoryStatus || "").toLowerCase() !== "in-warehouse") return false;
  const activation = (row.activationStatus || "pre-activation").toLowerCase();
  if (activation !== "pre-activation") return false;
  const enrollment = (row.enrollmentStatus || "not-enrolled").toLowerCase();
  if (enrollment !== "not-enrolled") return false;
  const ft = (row.firstTrustStatus || "none").toLowerCase();
  if (ft === "open" && !isExpiredOpen(row, nowMs)) return false;
  if (ft === "consumed") return false;
  return true;
}

/**
 * Factory-reset / credential-replacement eligibility on an existing enrolled serial.
 * Prefers an explicit pending re-enrollment signal from bootstrap; also allows
 * deliberate operator re-key on bound serials without a pending row.
 */
export function isReEnrollOpenEligible(
  row: FirstTrustInput,
  nowMs: number = Date.now(),
): boolean {
  const serial = (row.serialNumber || "").trim();
  if (!serial) return false;
  if (!isCredentialBound(row)) return false;
  const ft = (row.firstTrustStatus || "none").toLowerCase();
  if (ft === "open" && !isExpiredOpen(row, nowMs)) return false;
  return true;
}

/**
 * Canonical trust lifecycle for Device Assignments Status + filters.
 * NEW unbound serial → enrollment-required (never re-enrollment-required).
 * Bound serial with pending re-enrollment / open re-enroll window → re-enrollment-required.
 * Bound serial otherwise → enrolled.
 */
export function trustLifecycleStatus(
  row: FirstTrustInput,
  nowMs: number = Date.now(),
): TrustLifecycleStatus | null {
  const serial = (row.serialNumber || "").trim();
  if (!serial) return null;

  const bound = isCredentialBound(row);
  const pending = pendingKind(row);
  const ft = (row.firstTrustStatus || "none").toLowerCase();
  const purpose = (row.firstTrustPurpose || "").toLowerCase();
  const openReEnroll =
    bound &&
    ft === "open" &&
    !isExpiredOpen(row, nowMs) &&
    purpose === "re-enroll";

  if (!bound) {
    return "enrollment-required";
  }
  if (pending === "re-enrollment" || pending === "reenrollment" || openReEnroll) {
    return "re-enrollment-required";
  }
  return "enrolled";
}

export function trustLifecycleLabel(
  status: TrustLifecycleStatus | null | string,
): string {
  const key = (status || "").toLowerCase();
  if (key === "enrollment-required") return "ENROLLMENT REQUIRED";
  if (key === "re-enrollment-required") return "RE-ENROLLMENT REQUIRED";
  if (key === "enrolled") return "ENROLLED";
  return "";
}

export function firstTrustPhase(
  row: FirstTrustInput,
  nowMs: number = Date.now(),
): FirstTrustPhase {
  const serial = (row.serialNumber || "").trim();
  if (!serial) return "ineligible";

  const bound = isCredentialBound(row);
  const ft = (row.firstTrustStatus || "none").toLowerCase();

  // Bound serials may still have an open re-enroll window.
  if (bound) {
    if (ft === "open" && !isExpiredOpen(row, nowMs)) return "awaiting";
    if (isExpiredOpen(row, nowMs)) return "expired";
    if (ft === "revoked") return "revoked";
    return "enrolled";
  }

  if (ft === "revoked") return "revoked";
  if (isExpiredOpen(row, nowMs)) return "expired";
  if (ft === "open") return "awaiting";
  if (isFirstTrustOpenEligible(row, nowMs)) return "ready";
  return "not-enrolled";
}

export function firstTrustLabel(phase: FirstTrustPhase): string {
  switch (phase) {
    case "ready":
      return "ENROLLMENT REQUIRED";
    case "awaiting":
      return "AWAITING PLAYER";
    case "expired":
      return "EXPIRED";
    case "revoked":
      return "REVOKED";
    case "not-enrolled":
      return "ENROLLMENT REQUIRED";
    case "enrolled":
      return "ENROLLED";
    default:
      return "—";
  }
}

export function canOpenFirstTrust(row: FirstTrustInput, nowMs?: number): boolean {
  return isFirstTrustOpenEligible(row, nowMs);
}

export function canReEnrollFirstTrust(
  row: FirstTrustInput,
  nowMs?: number,
): boolean {
  return isReEnrollOpenEligible(row, nowMs);
}

export function canRevokeFirstTrust(row: FirstTrustInput, nowMs?: number): boolean {
  return firstTrustPhase(row, nowMs) === "awaiting";
}

/** Poll interval while First Trust modal is in Awaiting Player (bounded; not page-wide). */
export const FIRST_TRUST_AWAITING_POLL_MS = 3000;

/**
 * True while the modal holds an authorization result and is waiting for the
 * Player (Awaiting Player). Includes the brief gap after authorize before the
 * list row reflects firstTrustStatus=open. Stops on completion / revoke / expiry.
 */
export function isAwaitingFirstTrustPlayer(
  row: FirstTrustInput | null | undefined,
  authorized:
    | { serialNumber?: string; firstTrustWindowId?: string }
    | null
    | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!authorized || !row) return false;
  const serial = (row.serialNumber || "").trim();
  const authSerial = (authorized.serialNumber || "").trim();
  if (!serial || !authSerial || serial !== authSerial) return false;
  if (isFirstTrustPlayerComplete(row, authorized, nowMs)) return false;
  const phase = firstTrustPhase(row, nowMs);
  if (phase === "revoked" || phase === "expired") return false;
  // phase===awaiting, or authorized locally while list catch-up is in flight
  return phase === "awaiting" || Boolean(authorized);
}

/**
 * Authorization was issued and the Player consumed THAT window.
 * Matched by firstTrustWindowId so an already-enrolled row does not
 * false-complete before the open window is reflected.
 */
export function isFirstTrustPlayerComplete(
  row: FirstTrustInput | null | undefined,
  authorized:
    | { serialNumber?: string; firstTrustWindowId?: string }
    | null
    | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!authorized || !row) return false;
  const serial = (row.serialNumber || "").trim();
  const authSerial = (authorized.serialNumber || "").trim();
  if (!serial || !authSerial || serial !== authSerial) return false;
  const windowId = (authorized.firstTrustWindowId || "").trim();
  const rowWindow = (row.firstTrustWindowId || "").trim();
  if (!windowId || !rowWindow || windowId !== rowWindow) return false;
  if ((row.firstTrustStatus || "").toLowerCase() !== "consumed") return false;
  return firstTrustPhase(row, nowMs) === "enrolled";
}

export function firstTrustBadgeClass(phase: FirstTrustPhase): string {
  if (phase === "ready" || phase === "awaiting") return "maintenance";
  if (phase === "enrolled") return "online";
  return "offline";
}

export function trustLifecycleIcon(
  status: TrustLifecycleStatus | null,
): { icon: string; title: string } {
  if (status === "enrollment-required") {
    return { icon: "bi bi-shield-plus", title: "Enrollment Required" };
  }
  if (status === "re-enrollment-required") {
    return { icon: "bi bi-arrow-repeat", title: "Re-enrollment Required" };
  }
  if (status === "enrolled") {
    return { icon: "bi bi-shield-check", title: "Enrolled" };
  }
  return { icon: "bi bi-circle", title: "—" };
}

export function firstTrustIcon(phase: FirstTrustPhase): { icon: string; title: string } {
  switch (phase) {
    case "ready":
      return { icon: "bi bi-shield-plus", title: "Enrollment Required" };
    case "awaiting":
      return { icon: "bi bi-hourglass-split", title: "Awaiting Player" };
    case "enrolled":
      return { icon: "bi bi-shield-check", title: "Enrolled" };
    case "expired":
      return { icon: "bi bi-clock-history", title: "Expired" };
    case "revoked":
      return { icon: "bi bi-slash-circle", title: "Revoked" };
    case "not-enrolled":
      return { icon: "bi bi-shield", title: "Enrollment Required" };
    default:
      return { icon: "bi bi-dash-circle", title: "—" };
  }
}

export function ineligibleFirstTrustReason(row: FirstTrustInput): string {
  const serial = (row.serialNumber || "").trim() || "(no serial)";
  if (!(row.serialNumber || "").trim()) return `${serial}: missing serial`;
  if (isCredentialBound(row)) {
    return `${serial}: already enrolled / credential bound`;
  }
  if ((row.inventoryStatus || "").toLowerCase() !== "in-warehouse") {
    return `${serial}: not in-warehouse`;
  }
  if ((row.firstTrustStatus || "").toLowerCase() === "open") {
    return `${serial}: First Trust already open`;
  }
  if ((row.firstTrustStatus || "").toLowerCase() === "consumed") {
    return `${serial}: First Trust already consumed`;
  }
  return `${serial}: not eligible for First Trust`;
}

export function partitionFirstTrustSelection<T extends FirstTrustInput>(
  rows: T[],
  nowMs: number = Date.now(),
): { eligible: T[]; ineligible: T[] } {
  const eligible: T[] = [];
  const ineligible: T[] = [];
  for (const row of rows) {
    if (isFirstTrustOpenEligible(row, nowMs)) eligible.push(row);
    else ineligible.push(row);
  }
  return { eligible, ineligible };
}

/** Fixed Status filter options — enrollment lifecycle is always available. */
export const TRUST_LIFECYCLE_FILTER_OPTIONS = [
  { value: "enrollment-required", label: "Enrollment Required" },
  { value: "re-enrollment-required", label: "Re-enrollment Required" },
  { value: "enrolled", label: "Enrolled" },
] as const;
